/**
 * React Query hook for organization tenants
 *
 * Provides data fetching and mutations for organization tenant management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { OrganizationApiService } from '../services/organizationApi';
import { organizationKeys } from './useOrganization';
import type { OrganizationTenant } from '../types/organization';

/**
 * Hook return type for useOrganizationTenants
 */
export interface UseOrganizationTenantsResult {
  /** Organization tenants data */
  tenants: OrganizationTenant[] | undefined;
  /** Loading state for initial fetch */
  isLoading: boolean;
  /** Fetching state (true during refetch) */
  isFetching: boolean;
  /** Error from fetching tenants */
  error: Error | null;
  /** Assign a tenant to the organization */
  assignTenant: (tenantDbId: number) => Promise<OrganizationTenant>;
  /** Loading state for assign mutation */
  isAssigning: boolean;
  /** Error from assign mutation */
  assignError: Error | null;
  /** Remove a tenant from the organization */
  removeTenant: (tenantDbId: number) => Promise<void>;
  /** Loading state for remove mutation */
  isRemoving: boolean;
  /** Error from remove mutation */
  removeError: Error | null;
  /** Refetch tenants data */
  refetch: () => void;
}

/**
 * React Query hook for fetching and managing organization tenants
 *
 * @param orgId - Organization ID, or undefined to skip fetching
 * @returns Tenants data, loading states, and mutation functions
 *
 * @example
 * ```tsx
 * const { tenants, isLoading, assignTenant, removeTenant } = useOrganizationTenants(1);
 *
 * // Assign a tenant
 * await assignTenant(tenantDbId);
 *
 * // Remove a tenant
 * await removeTenant(tenantDbId);
 * ```
 */
export function useOrganizationTenants(orgId: number | undefined): UseOrganizationTenantsResult {
  const queryClient = useQueryClient();

  // Query for fetching organization tenants
  const {
    data: tenants,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: organizationKeys.tenants(orgId!),
    queryFn: () => OrganizationApiService.listOrganizationTenants(orgId!),
    enabled: orgId !== undefined,
  });

  // Mutation for assigning a tenant
  const {
    mutateAsync: assignTenant,
    isPending: isAssigning,
    error: assignError,
  } = useMutation({
    mutationFn: (tenantDbId: number) =>
      OrganizationApiService.assignTenantToOrganization(orgId!, tenantDbId),
    onSuccess: () => {
      // Invalidate both tenants list and organization (for updated counts)
      queryClient.invalidateQueries({ queryKey: organizationKeys.tenants(orgId!) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(orgId!) });
    },
  });

  // Mutation for removing a tenant
  const {
    mutateAsync: removeTenant,
    isPending: isRemoving,
    error: removeError,
  } = useMutation({
    mutationFn: (tenantDbId: number) =>
      OrganizationApiService.removeTenantFromOrganization(orgId!, tenantDbId),
    onSuccess: () => {
      // Invalidate both tenants list and organization (for updated counts)
      queryClient.invalidateQueries({ queryKey: organizationKeys.tenants(orgId!) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(orgId!) });
    },
  });

  return {
    tenants,
    isLoading,
    isFetching,
    error: error as Error | null,
    assignTenant,
    isAssigning,
    assignError: assignError as Error | null,
    removeTenant,
    isRemoving,
    removeError: removeError as Error | null,
    refetch,
  };
}
